<template>
  <div class="contents">
    <div class="container-fluid">
      <div class="breadcrumb-main user-member justify-content-sm-between mb-3">
        <h4 class="text-capitalize fw-500 breadcrumb-title">
          <a @click="$router.go(-1)" style="cursor:pointer"><i class="fas fa-arrow-left"></i></a>
          &nbsp; Setting Toko
        </h4>
      </div>

      <div class="card p-4 border bg-white radius-xl mb-3" v-if="!isLoading">
        <h5>Identitas & Branding</h5>
        <div class="row">
          <div class="col-md-6 mb-2">
            <label>Nomor HP Owner</label>
            <input v-model="form.owner_phone" class="form-control" placeholder="08xxxxxxxxxx">
          </div>
        </div>
        <label>Alamat Owner / Toko</label>
        <textarea v-model="form.owner_address" class="form-control mb-3" rows="2"></textarea>

        <hr>
        <h5><i class="fas fa-palette text-primary"></i> Tampilan & Warna Toko</h5>
        <p class="text-muted mb-3">
          Sesuaikan warna dan gaya tampilan toko online Anda. Customer akan melihat perubahan di halaman katalog, keranjang, checkout, donasi, dan kursus.
        </p>
        <ShopThemeCustomizer v-model="themeForm" />

        <hr>
        <label>Custom Domain (opsional)</label>
        <input v-model="form.custom_domain" class="form-control mb-2" placeholder="tokoanda.com" @input="domainStatus = 'idle'">
        <div class="alert alert-light border p-3 mb-2">
          <p class="mb-2"><b>Langkah aktivasi otomatis:</b></p>
          <ol class="mb-2 pl-3">
            <li>Simpan domain di bawah, lalu buat record DNS di provider domain Anda:</li>
          </ol>
          <p class="mb-1 small"><b>Subdomain</b> (www atau subdomain, mis. <code>www</code>):</p>
          <code class="d-block bg-white p-2 border rounded mb-2">CNAME &nbsp; www &nbsp; → &nbsp; app.starsender.online</code>
          <p class="mb-1 small"><b>Domain root/apex</b> (@ tanpa www, mis. <code>{{ form.custom_domain || 'tokoanda.com' }}</code>):</p>
          <code class="d-block bg-white p-2 border rounded mb-2">A &nbsp; @ &nbsp; → &nbsp; {{ customDomainTargetIP }}</code>
          <div class="alert alert-info py-2 px-3 mb-2">
            <small>IP server: <b>{{ customDomainTargetIP }}</b> — sama dengan <code>app.starsender.online</code>.<br>
            Jika provider mendukung ALIAS/ANAME di root, bisa arahkan ke <code>app.starsender.online</code> sebagai alternatif A record.</small>
          </div>
          <div class="d-flex align-items-center flex-wrap" style="gap:8px;">
            <button type="button" class="btn btn-outline-primary btn-sm" @click="verifyDomain" :disabled="!form.custom_domain || isVerifyingDomain">
              <span v-if="isVerifyingDomain" class="spinner-border spinner-border-sm mr-1"></span>
              Verifikasi DNS
            </button>
            <span v-if="domainStatus === 'verified'" class="badge badge-success">✓ Terverifikasi — domain aktif</span>
            <span v-else-if="domainStatus === 'pending'" class="badge badge-warning">Menunggu verifikasi DNS</span>
            <span v-else-if="domainStatus === 'failed'" class="badge badge-danger">Verifikasi gagal</span>
            <span v-if="provisionStatus === 'pending'" class="badge badge-info">SSL sedang dipasang…</span>
            <span v-else-if="provisionStatus === 'success'" class="badge badge-success">SSL aktif</span>
            <span v-else-if="provisionStatus === 'failed'" class="badge badge-secondary">SSL perlu setup manual</span>
          </div>
          <small v-if="domainMessage" class="d-block mt-2" :class="domainStatus === 'failed' ? 'text-danger' : 'text-muted'">{{ domainMessage }}</small>
        </div>

        <hr>
        <h5>WhatsApp Pengirim</h5>
        <label>Device WA Pengirim Notifikasi</label>
        <select v-model.number="form.device_id" class="form-control mb-3">
          <option :value="null">-- Pilih device --</option>
          <option v-for="d in devices" :key="d.id" :value="d.id">{{ d.name }} ({{ d.no_hp }})</option>
        </select>

        <hr>
        <h5>Pembayaran</h5>
        <label>Info Rekening Bank (untuk Transfer Manual)</label>
        <textarea v-model="form.bank_account" class="form-control mb-2" rows="3"
          placeholder="BCA 7370673556 a.n. Yohanes&#10;Mandiri 1230005692423 a.n. Yohanes"></textarea>
        <label>Metode Pembayaran Aktif</label>
        <div class="row mb-2">
          <div class="col-md-6" v-for="m in availablePaymentMethods" :key="m.value">
            <div class="form-check">
              <input :id="'pm_'+m.value" type="checkbox" class="form-check-input"
                :checked="paymentMethodsList.includes(m.value)" @change="togglePaymentMethod(m.value)">
              <label :for="'pm_'+m.value" class="form-check-label">{{ m.label }}</label>
            </div>
          </div>
        </div>

        <hr>
        <h5>Pengiriman (Ongkir)</h5>

        <!-- ORIGIN SEARCH -->
        <label>Lokasi Asal Pengiriman (untuk hitung ongkir)</label>
        <div v-if="originSelected" class="alert alert-success p-2 mb-2 d-flex justify-content-between align-items-center">
          <div>
            <small class="d-block text-muted">📍 Origin terpilih:</small>
            <b>{{ originSelected.subdistrict_name }}, {{ originSelected.city_name }}, {{ originSelected.province_name }}</b>
            <small class="d-block text-muted">ID: {{ originSelected.id }}</small>
          </div>
          <button class="btn btn-sm btn-light" @click="clearOrigin"><i class="fas fa-times"></i> Ganti</button>
        </div>
        <div v-else class="position-relative mb-2">
          <input v-model="originSearch" class="form-control" placeholder="Ketik nama kelurahan/kecamatan..." @input="searchOrigin">
          <div v-if="originResults.length > 0" class="border rounded position-absolute w-100 bg-white shadow-sm" style="z-index:100; max-height:300px; overflow-y:auto;">
            <div v-for="d in originResults" :key="d.id" class="p-2 border-bottom" style="cursor:pointer" @click="selectOrigin(d)">
              <b>{{ d.subdistrict_name }}</b><br>
              <small class="text-muted">{{ d.city_name }}, {{ d.province_name }}</small>
            </div>
          </div>
          <small v-if="originSelectedID && !originResults.length" class="text-warning">⚠️ Origin ID lama: {{ originSelectedID }} — search ulang untuk konfirmasi.</small>
        </div>

        <!-- KURIR CHECKBOXES -->
        <label class="mt-2">Kurir Tersedia</label>
        <div class="row mb-2">
          <div class="col-md-4" v-for="c in availableCouriers" :key="c.value">
            <div class="form-check">
              <input :id="'kr_'+c.value" type="checkbox" class="form-check-input"
                :checked="courierList.includes(c.value)" @change="toggleCourier(c.value)">
              <label :for="'kr_'+c.value" class="form-check-label">{{ c.label }}</label>
            </div>
          </div>
        </div>

        <!-- FREE ONGKIR -->
        <label class="mt-2">Free Ongkir untuk Min Pembelian</label>
        <input v-model.number="form.free_shipping_min" type="number" class="form-control mb-2" placeholder="0 = tidak aktif">
        <small class="text-muted d-block mb-3">Set 0 untuk nonaktifkan. Misal isi 100000 = customer beli min Rp 100rb dapat free ongkir.</small>

        <!-- COD AREAS CHIP LIST -->
        <label class="mt-2">Area COD (Cash on Delivery)</label>
        <div class="d-flex flex-wrap mb-2" v-if="codAreasList.length > 0" style="gap:6px">
          <span v-for="(a, i) in codAreasList" :key="i" class="badge badge-primary p-2" style="font-size:13px">
            {{ a }}
            <button class="btn btn-sm p-0 ml-2 text-white" @click="removeCodArea(i)" style="background:transparent;border:none">
              <i class="fas fa-times"></i>
            </button>
          </span>
        </div>
        <div class="position-relative">
          <input v-model="codSearch" class="form-control" placeholder="Cari kota → klik untuk tambah" @input="searchCod">
          <div v-if="codResults.length > 0" class="border rounded position-absolute w-100 bg-white shadow-sm" style="z-index:99; max-height:200px; overflow-y:auto;">
            <div v-for="d in codResults" :key="d.id" class="p-2 border-bottom" style="cursor:pointer" @click="addCodArea(d.city_name)">
              <b>{{ d.city_name }}</b><small class="text-muted ml-2">{{ d.province_name }}</small>
            </div>
          </div>
        </div>
        <small class="text-muted d-block mt-1">Atau ketik manual nama kota lalu Enter</small>

        <hr>
        <h5>Donasi</h5>
        <p class="text-muted mb-2">
          Campaign donasi dikelola di menu <b>Produk</b> bersama daftar produk toko.
          Aktifkan/nonaktifkan di sana, lalu atur judul, target, dan nominal preset.
        </p>
        <router-link :to="'/shop/' + shopId + '?tab=produk'" class="btn btn-outline-primary btn-sm">
          <i class="fas fa-box"></i> Kelola Donasi di Produk
        </router-link>

        <hr>
        <h5>LMS / Kursus Online</h5>
        <p class="text-muted mb-2">
          Fitur kursus online dikelola di menu <b>Produk</b> bersama daftar produk toko.
          Aktifkan/nonaktifkan di sana, lalu buat kursus dengan modul &amp; pelajaran.
        </p>
        <router-link :to="'/shop/' + shopId + '?tab=produk'" class="btn btn-outline-primary btn-sm">
          <i class="fas fa-graduation-cap"></i> Kelola Kursus di Produk
        </router-link>

        <hr>
        <h5><i class="fas fa-chart-line text-primary"></i> Tracking &amp; Pixel Iklan</h5>
        <p class="text-muted mb-3">
          Pasang pixel Facebook, Google Tag Manager, dan TikTok Ads di toko online Anda untuk iklan retargeting,
          catalog ads, dan conversion tracking. Pixel hanya dimuat di halaman publik toko (<code>/toko/slug-anda</code>)
          saat diaktifkan.
        </p>

        <div class="card border mb-3">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 class="mb-0"><i class="fab fa-facebook text-primary"></i> Facebook Pixel</h6>
                <small class="text-muted">Untuk Meta Ads &amp; catalog produk</small>
              </div>
              <div class="custom-control custom-switch">
                <input type="checkbox" class="custom-control-input" id="fb_enabled" v-model="form.facebook_pixel_enabled">
                <label class="custom-control-label" for="fb_enabled">{{ form.facebook_pixel_enabled ? 'Aktif' : 'Nonaktif' }}</label>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6 mb-2">
                <label>Facebook Pixel ID</label>
                <input v-model="form.facebook_pixel_id" class="form-control" placeholder="123456789012345" :disabled="!form.facebook_pixel_enabled">
                <small class="text-muted">Dari Events Manager → Data Sources → Pixel ID</small>
              </div>
              <div class="col-md-6 mb-2">
                <label>Conversions API Token <span class="text-muted">(opsional, advanced)</span></label>
                <input v-model="form.facebook_capi_token" type="password" class="form-control" placeholder="Token CAPI untuk server-side events" autocomplete="new-password">
                <small class="text-muted">Disimpan aman. Digunakan untuk server-side Purchase events (coming soon).</small>
              </div>
            </div>
          </div>
        </div>

        <div class="card border mb-3">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 class="mb-0"><i class="fab fa-google text-danger"></i> Google Tag Manager</h6>
                <small class="text-muted">Container GTM untuk Google Ads &amp; Analytics tags</small>
              </div>
              <div class="custom-control custom-switch">
                <input type="checkbox" class="custom-control-input" id="gtm_enabled" v-model="form.gtm_enabled">
                <label class="custom-control-label" for="gtm_enabled">{{ form.gtm_enabled ? 'Aktif' : 'Nonaktif' }}</label>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6 mb-2">
                <label>GTM Container ID</label>
                <input v-model="form.gtm_container_id" class="form-control" placeholder="GTM-XXXXXXX" :disabled="!form.gtm_enabled">
                <small class="text-muted">Format: GTM-XXXXXXX dari tagmanager.google.com</small>
              </div>
            </div>
            <div class="alert alert-light border small mb-0 mt-2">
              <b>Events dataLayer:</b> page_view, view_content, add_to_cart, begin_checkout, purchase, search, view_category —
              siap untuk custom tags di GTM.
            </div>
          </div>
        </div>

        <div class="card border mb-3">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 class="mb-0"><i class="fab fa-tiktok"></i> TikTok Ads Pixel</h6>
                <small class="text-muted">Untuk TikTok Ads Manager &amp; catalog</small>
              </div>
              <div class="custom-control custom-switch">
                <input type="checkbox" class="custom-control-input" id="tt_enabled" v-model="form.tiktok_pixel_enabled">
                <label class="custom-control-label" for="tt_enabled">{{ form.tiktok_pixel_enabled ? 'Aktif' : 'Nonaktif' }}</label>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6 mb-2">
                <label>TikTok Pixel ID</label>
                <input v-model="form.tiktok_pixel_id" class="form-control" placeholder="CXXXXXXXXXXXXXXX" :disabled="!form.tiktok_pixel_enabled">
                <small class="text-muted">Dari TikTok Ads Manager → Events → Web Events → Pixel Code</small>
              </div>
            </div>
          </div>
        </div>

        <hr>
        <h5>📧 Email Pengirim</h5>
        <p class="text-muted">
          Default pakai email bawaan StarSender (<b>info@starfield.id</b>).
          Isi API token Mailketing sendiri jika ingin kirim dari akun &amp; domain verified Anda.
        </p>
        <div class="row">
          <div class="col-md-6 mb-2">
            <label>Custom Email Sender</label>
            <input v-model="form.custom_email_sender" class="form-control" placeholder="cs@tokoaku.com">
            <small class="text-muted">Wajib jika pakai API token Mailketing sendiri. Domain harus verified di Mailketing.</small>
          </div>
          <div class="col-md-6 mb-2">
            <label>Custom Email Name</label>
            <input v-model="form.custom_email_name" class="form-control" placeholder="Toko Aku">
            <small class="text-muted">Nama pengirim yang tampil di inbox customer.</small>
          </div>
        </div>
        <div class="row">
          <div class="col-md-8 mb-2">
            <label>Mailketing API Token <span class="text-muted">(opsional)</span></label>
            <input v-model="form.mailketing_api_token" type="password" class="form-control" placeholder="Kosongkan = pakai email bawaan StarSender" autocomplete="new-password">
            <small class="text-muted d-block mt-1">
              Token dari Dashboard Mailketing → API Integration.
              <a href="https://api.mailketing.co.id/docs/" target="_blank" rel="noopener">Dokumentasi Mailketing API</a>
            </small>
          </div>
          <div class="col-md-4 mb-2 d-flex align-items-end">
            <button type="button" class="btn btn-outline-secondary w-100" @click="testEmail" :disabled="isTestingEmail">
              <span v-if="isTestingEmail" class="spinner-border spinner-border-sm mr-1"></span>
              Test Kirim Email
            </button>
          </div>
        </div>

        <div class="text-right mt-4">
          <button class="btn btn-primary btn-lg" @click="save" :disabled="isSaving">
            <span v-if="isSaving" class="spinner-border spinner-border-sm mr-2"></span>
            💾 Simpan Settings
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import ShopThemeCustomizer from '@/components/shop/ShopThemeCustomizer.vue';
import { DEFAULT_THEME } from '@/components/shop_public/shop_public_utils';

function parsePaymentMethods(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    try { return JSON.parse(v) || []; } catch (e) { return []; }
  }
  return [];
}

export default {
  components: { ShopThemeCustomizer },
  data() {
    return {
      shopId: null,
      customDomainTargetIP: '103.160.212.22',
      isLoading: false,
      isSaving: false,
      isTestingEmail: false,
      isVerifyingDomain: false,
      domainStatus: 'idle',
      domainMessage: '',
      provisionStatus: 'none',
      devices: [],
      // Origin search
      originSearch: '',
      originResults: [],
      originSelected: null,    // {id, subdistrict_name, city_name, province_name}
      originSelectedID: null,  // raw ID dari DB kalau belum di-resolve
      // COD search
      codSearch: '',
      codResults: [],
      // Search debounce timers
      originTimer: null,
      codTimer: null,
      availablePaymentMethods: [
        { value: 'transfer_manual', label: 'Transfer Bank Manual' },
        { value: 'tripay_qris', label: 'QRIS (Tripay)' },
        { value: 'tripay_va', label: 'Virtual Account (Tripay)' },
        { value: 'tripay_ewallet', label: 'E-Wallet (OVO/DANA/ShopeePay) - Tripay' },
        { value: 'cod', label: 'Cash on Delivery (COD)' },
      ],
      availableCouriers: [
        { value: 'jne', label: 'JNE' },
        { value: 'jnt', label: 'J&T Express' },
        { value: 'sicepat', label: 'SiCepat' },
        { value: 'pos', label: 'Pos Indonesia' },
        { value: 'tiki', label: 'TIKI' },
        { value: 'anteraja', label: 'Anteraja' },
        { value: 'wahana', label: 'Wahana' },
        { value: 'ninja', label: 'Ninja Express' },
        { value: 'lion', label: 'Lion Parcel' },
      ],
      form: {
        bank_account: '', owner_phone: '', owner_address: '', device_id: null,
        shipping_origin_id: null, courier_enabled: '', cod_areas: '',
        free_shipping_min: 0, payment_methods: [], theme_color: '#1976d2',
        theme_accent_color: '', theme_preset: '', theme_header_style: 'gradient', theme_button_style: 'rounded',
        custom_email_sender: '', custom_email_name: '',         custom_domain: '',
        mailketing_api_token: '',
        facebook_pixel_id: '', facebook_pixel_enabled: false, facebook_capi_token: '',
        gtm_container_id: '', gtm_enabled: false,
        tiktok_pixel_id: '', tiktok_pixel_enabled: false,
      },
    };
  },
  computed: {
    paymentMethodsList() {
      return Array.isArray(this.form.payment_methods) ? this.form.payment_methods : [];
    },
    courierList() {
      if (!this.form.courier_enabled) return [];
      return this.form.courier_enabled.split(':').map(s => s.trim()).filter(Boolean);
    },
    codAreasList() {
      if (!this.form.cod_areas) return [];
      return this.form.cod_areas.split(',').map(s => s.trim()).filter(Boolean);
    },
    themeForm: {
      get() {
        return {
          theme_color: this.form.theme_color,
          theme_accent_color: this.form.theme_accent_color,
          theme_preset: this.form.theme_preset,
          theme_header_style: this.form.theme_header_style,
          theme_button_style: this.form.theme_button_style,
        };
      },
      set(v) {
        this.form.theme_color = v.theme_color || DEFAULT_THEME.theme_color;
        this.form.theme_accent_color = v.theme_accent_color || '';
        this.form.theme_preset = v.theme_preset || '';
        this.form.theme_header_style = v.theme_header_style || 'gradient';
        this.form.theme_button_style = v.theme_button_style || 'rounded';
      },
    },
  },
  mounted() {
    this.shopId = this.$route.params.id;
    this.load();
    this.loadDevices();
  },
  methods: {
    async loadDevices() {
      try {
        const r = await this.$store.dispatch('device_list_all');
        this.devices = (r.data.data.devices || []).filter(d => d.status === 'connected');
      } catch (e) { /* noop */ }
    },
    async load() {
      this.isLoading = true;
      try {
        const r = await this.$store.dispatch('shop/shop_get_settings', this.shopId);
        const s = r.data.data.shop;
        this.form = {
          bank_account: s.bank_account || '',
          owner_phone: s.owner_phone || '',
          owner_address: s.owner_address || '',
          device_id: s.device_id || null,
          shipping_origin_id: s.shipping_origin_id || null,
          courier_enabled: s.courier_enabled || '',
          cod_areas: s.cod_areas || '',
          free_shipping_min: s.free_shipping_min || 0,
          payment_methods: parsePaymentMethods(s.payment_methods),
          theme_color: s.theme_color || '#1976d2',
          theme_accent_color: s.theme_accent_color || '',
          theme_preset: s.theme_preset || '',
          theme_header_style: s.theme_header_style || 'gradient',
          theme_button_style: s.theme_button_style || 'rounded',
          custom_email_sender: s.custom_email_sender || '',
          custom_email_name: s.custom_email_name || '',
          custom_domain: s.custom_domain || '',
          mailketing_api_token: s.mailketing_api_token || '',
          facebook_pixel_id: s.facebook_pixel_id || '',
          facebook_pixel_enabled: !!s.facebook_pixel_enabled,
          facebook_capi_token: s.facebook_capi_token || '',
          gtm_container_id: s.gtm_container_id || '',
          gtm_enabled: !!s.gtm_enabled,
          tiktok_pixel_id: s.tiktok_pixel_id || '',
          tiktok_pixel_enabled: !!s.tiktok_pixel_enabled,
        };
        this.domainStatus = s.custom_domain_verified ? 'verified' : (s.custom_domain ? 'pending' : 'idle');
        this.provisionStatus = s.custom_domain_provision_status || 'none';
        this.domainMessage = s.custom_domain_verified ? 'Domain sudah terverifikasi.' : '';
        this.originSelectedID = s.shipping_origin_id || null;
      } catch (e) { this.$toast.error('Gagal load'); }
      this.isLoading = false;
    },
    togglePaymentMethod(value) {
      const idx = this.form.payment_methods.indexOf(value);
      if (idx >= 0) this.form.payment_methods.splice(idx, 1);
      else this.form.payment_methods.push(value);
    },
    toggleCourier(value) {
      const list = this.courierList;
      const idx = list.indexOf(value);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(value);
      this.form.courier_enabled = list.join(':');
    },
    searchOrigin() {
      clearTimeout(this.originTimer);
      if (this.originSearch.length < 3) { this.originResults = []; return; }
      this.originTimer = setTimeout(async () => {
        try {
          const r = await axios.get('shop/shipping/destinations', { params: { q: this.originSearch } });
          this.originResults = r.data.data.destinations || [];
        } catch (e) { /* noop */ }
      }, 400);
    },
    selectOrigin(d) {
      this.originSelected = d;
      this.form.shipping_origin_id = d.id;
      this.originSearch = '';
      this.originResults = [];
    },
    clearOrigin() {
      this.originSelected = null;
      this.form.shipping_origin_id = null;
      this.originSelectedID = null;
    },
    searchCod() {
      clearTimeout(this.codTimer);
      if (this.codSearch.length < 3) { this.codResults = []; return; }
      this.codTimer = setTimeout(async () => {
        try {
          const r = await axios.get('shop/shipping/destinations', { params: { q: this.codSearch } });
          // dedup by city_name
          const seen = new Set();
          this.codResults = (r.data.data.destinations || []).filter(d => {
            if (seen.has(d.city_name)) return false;
            seen.add(d.city_name); return true;
          }).slice(0, 10);
        } catch (e) { /* noop */ }
      }, 400);
    },
    addCodArea(name) {
      const list = this.codAreasList;
      if (!list.includes(name)) {
        list.push(name);
        this.form.cod_areas = list.join(',');
      }
      this.codSearch = '';
      this.codResults = [];
    },
    removeCodArea(i) {
      const list = this.codAreasList;
      list.splice(i, 1);
      this.form.cod_areas = list.join(',');
    },
    async save() {
      this.isSaving = true;
      try {
        const payload = { ...this.form };
        if (Array.isArray(payload.payment_methods)) {
          payload.payment_methods = JSON.stringify(payload.payment_methods);
        }
        await this.$store.dispatch('shop/shop_update_settings', { id: this.shopId, settings: payload });
        this.$toast.success('Settings tersimpan');
        const refreshed = await this.$store.dispatch('shop/shop_get_settings', this.shopId);
        const s = refreshed.data.data.shop;
        this.form.mailketing_api_token = s.mailketing_api_token || '';
        this.form.facebook_capi_token = s.facebook_capi_token || '';
      } catch (e) { this.$toast.error(e.response?.data?.message || 'Gagal save'); }
      this.isSaving = false;
    },
    async testEmail() {
      this.isTestingEmail = true;
      try {
        const r = await this.$store.dispatch('shop/shop_test_email', {
          id: this.shopId,
          recipient: null,
        });
        this.$toast.success(r.data.message || 'Test email terkirim');
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Gagal kirim test email');
      }
      this.isTestingEmail = false;
    },
    async verifyDomain() {
      if (!this.form.custom_domain) {
        this.$toast.error('Isi domain dulu');
        return;
      }
      this.isVerifyingDomain = true;
      this.domainMessage = '';
      try {
        await this.$store.dispatch('shop/shop_update_settings', {
          id: this.shopId,
          settings: { custom_domain: this.form.custom_domain },
        });
        const r = await this.$store.dispatch('shop/shop_verify_domain', this.shopId);
        const data = r.data.data || {};
        if (r.data.success) {
          this.domainStatus = 'verified';
          this.provisionStatus = data.provision_status || 'pending';
          this.domainMessage = r.data.message || 'Domain terverifikasi';
          this.$toast.success('DNS terverifikasi!');
        } else {
          this.domainStatus = 'failed';
          const hint = data.dns_hint;
          if (hint && typeof hint === 'object') {
            this.domainMessage = [r.data.message, hint.cname, hint.a].filter(Boolean).join(' — ');
          } else {
            this.domainMessage = r.data.message || 'DNS belum benar';
          }
          this.$toast.warning(this.domainMessage);
        }
      } catch (e) {
        this.domainStatus = 'failed';
        this.domainMessage = e.response?.data?.message || 'Gagal verifikasi';
        this.$toast.error(this.domainMessage);
      }
      this.isVerifyingDomain = false;
    },
  },
};
</script>
